<template>
  <div class="export-panel">
    <div class="summary">
      <div class="summary-item">
        <span class="summary-label">Écritures à exporter</span>
        <span class="summary-value">{{ exportedData ? exportedData.totals.count : '-' }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Total débit exporté</span>
        <span class="summary-value">{{ exportedData ? money(exportedData.totals.debit) : '-' }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Total crédit exporté</span>
        <span class="summary-value">{{ exportedData ? money(exportedData.totals.credit) : '-' }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">SIREN</span>
        <span class="summary-value">{{ exportData && exportData.company.siren ? exportData.company.siren : '-' }}</span>
      </div>
      <div class="actions">
        <button class="export-button" :disabled="!canExport" @click="exportFec" title="Fichier des Écritures Comptables de la période, limité aux journaux sélectionnés (.txt tabulé)">
          <span class="fas fa-file-lines"></span>Export FEC<span v-if="scopeLabel" class="button-scope">{{ scopeLabel }}</span>
        </button>
        <button class="export-button" :disabled="!canExport" @click="exportExcel" title="Classeur Excel de la période, écritures limitées aux journaux sélectionnés : écritures, journaux, balance des tiers, TVA, contrôles">
          <span class="fas fa-file-excel"></span>Export Excel<span v-if="scopeLabel" class="button-scope">{{ scopeLabel }}</span>
        </button>
      </div>
    </div>

    <AccountingControls v-if="exportData" :controls="exportData.controls" />

    <div class="help">
      <p>
        Fichier complet de la période : <strong>ventes</strong> (journal VT : factures, factures d'acompte, avoirs), <strong>déduction des acomptes</strong> (OD)<template v-if="hasPurchases">,
        <strong>achats</strong> (journal AC)</template>
        et <strong>trésorerie</strong> (BQ banque pour virements, chèques, cartes et prélèvements, CA caisse).
      </p>
      <ul>
        <li><strong>706200</strong> : ventes avec TVA (prestations de services)</li>
        <li><strong>706000</strong> : ventes sans TVA</li>
        <li><strong>4457</strong> : TVA collectée, un compte par taux</li>
        <li><strong>4191</strong> : acomptes</li>
        <li><strong>411</strong> + nom du client : un compte par client (ex. 411DUPONT3A1)</li>
        <template v-if="hasPurchases">
          <li><strong>601, 607, 611, 624...</strong> : achats, dans le compte de charge de leur catégorie (les achats à vérifier ne sont pas comptabilisés)</li>
          <li><strong>445660</strong> : TVA déductible</li>
          <li><strong>401</strong> : fournisseurs (compte auxiliaire par fournisseur)</li>
        </template>
      </ul>
      <p>
        Les écritures d'un même contrat<template v-if="hasPurchases"> ou d'un même achat</template> sont <strong>lettrées</strong> lorsqu'elles se soldent.
        Le classeur Excel ajoute la synthèse, la balance des tiers, la TVA (facturée<template v-if="hasPurchases">, déductible</template> et sur encaissements<template v-if="hasPurchases"> / décaissements</template>) et les contrôles.
      </p>
    </div>

    <div v-if="exportData && exportData.entries.length > 0" class="filters">
      <div class="filter-group export-group">
        <div class="filter-title">
          <span class="fas fa-file-export"></span>Journaux à exporter
          <span class="filter-tag export-tag">S'applique à l'export FEC et Excel</span>
        </div>
        <div class="journals">
          <button class="chip" :class="{active: selectedJournals.length === 0}" :aria-pressed="selectedJournals.length === 0" @click="selectedJournals = []">Tous les journaux</button>
          <button v-for="journal in exportData.journals" :key="journal.code" class="chip" :class="{active: selectedJournals.includes(journal.code)}" :aria-pressed="selectedJournals.includes(journal.code)" @click="toggleJournal(journal.code)">
            {{ journal.code }} · {{ journal.label }} <span class="chip-count">{{ journal.count }}</span>
          </button>
        </div>
        <p class="export-scope">
          <template v-if="exportedData.selectedJournals">
            Seuls les journaux <strong>{{ exportedData.selectedJournals.join(', ') }}</strong> seront exportés ({{ exportedData.totals.count }} écritures). Un FEC limité à certains journaux est partiel, il ne remplace pas le FEC complet de l'exercice.
          </template>
          <template v-else>
            Tous les journaux seront exportés ({{ exportedData.totals.count }} écritures). Cliquez sur un ou plusieurs journaux pour limiter l'export.
          </template>
        </p>
      </div>
      <div class="filter-group">
        <div class="filter-title">
          <span class="fas fa-eye"></span>Aperçu ci-dessous
          <span class="filter-tag">Sans effet sur l'export</span>
        </div>
        <div class="journals">
          <button class="chip" :class="{active: onlyUnlettered}" @click="onlyUnlettered = !onlyUnlettered" title="Écritures dont le compte client ou fournisseur n'est pas lettré">
            Non lettrées <span class="chip-count">{{ unletteredCount }}</span>
          </button>
          <input v-model="search" class="search" type="search" placeholder="Rechercher (client, pièce, libellé, compte)" />
        </div>
      </div>
    </div>

    <div class="table-container">
      <table class="entries">
        <colgroup>
          <col style="width: 4%"><col style="width: 7%"><col style="width: 5%"><col style="width: 19%"><col style="width: 13%">
          <col style="width: 8%"><col style="width: 22%"><col style="width: 8%"><col style="width: 8%"><col style="width: 6%">
        </colgroup>
        <thead>
        <tr>
          <th>N°</th>
          <th>Date</th>
          <th>Journal</th>
          <th>Compte</th>
          <th>Auxiliaire</th>
          <th>Pièce</th>
          <th>Libellé</th>
          <th class="right">Débit</th>
          <th class="right">Crédit</th>
          <th>Lettrage</th>
        </tr>
        </thead>
        <tbody v-if="isLoading"><tr class="empty-row"><td colspan="10">Chargement...</td></tr></tbody>
        <tbody v-else-if="exportData && visibleEntries.length > 0">
        <tr v-for="(entry, index) in visibleEntries" :key="index" :class="{alt: entry.ecritureNum % 2 === 0}">
          <td>{{ entry.ecritureNum }}</td>
          <td>{{ displayDate(entry.ecritureDate) }}</td>
          <td>{{ entry.journalCode }}</td>
          <td :title="`${entry.compteNum} ${entry.compteLib}`"><strong>{{ entry.compteNum }}</strong> {{ entry.compteLib }}</td>
          <td :title="`${entry.compAuxNum} ${entry.compAuxLib}`">{{ entry.compAuxNum }}<span v-if="entry.compAuxNum" class="aux-lib"> {{ entry.compAuxLib }}</span></td>
          <td :title="entry.pieceRef"><span class="doc-chip">{{ entry.pieceRef }}</span></td>
          <td :title="entry.ecritureLib">{{ entry.ecritureLib }}</td>
          <td class="right">{{ entry.debit ? money(entry.debit) : '' }}</td>
          <td class="right">{{ entry.credit ? money(entry.credit) : '' }}</td>
          <td>{{ entry.ecritureLet }}</td>
        </tr>
        <tr v-if="hiddenCount > 0" class="more-row">
          <td colspan="10"><button class="more-button" @click="visibleCount += PAGE_SIZE">Afficher {{ Math.min(PAGE_SIZE, hiddenCount) }} écritures de plus ({{ hiddenCount }} masquées)</button></td>
        </tr>
        <tr class="total">
          <td colspan="7">Total ({{ displayedGroups.length }} écriture{{ displayedGroups.length > 1 ? 's' : '' }}{{ isFiltered ? ' filtrées' : '' }})</td>
          <td class="right">{{ money(displayedTotals.debit) }}</td>
          <td class="right">{{ money(displayedTotals.credit) }}</td>
          <td></td>
        </tr>
        </tbody>
        <tbody v-else><tr class="empty-row"><td colspan="10">Aucune écriture sur la période sélectionnée</td></tr></tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {library} from "@fortawesome/fontawesome-svg-core";
import {faFileExcel, faFileLines} from "@fortawesome/free-solid-svg-icons";
import {AccountingController} from "@/accounting/controllers/AccountingController";
import {AccountingService} from "@/accounting/services/AccountingService";
import AccountingControls from "@/accounting/components/AccountingControls.vue";
import {displayDate, money} from "@/accounting/services/export/AccountingFormat";
import {downloadFec} from "@/accounting/services/export/FecExport";
import {downloadExcel} from "@/accounting/services/export/ExcelExport";
import {restrictToJournals} from "@/accounting/services/export/JournalSelection";

library.add(faFileLines, faFileExcel);

// Nombre d'écritures affichées dans l'aperçu, et ajoutées à chaque clic sur « Afficher plus »
const PAGE_SIZE = 200;
// Comptes de tiers lettrés : clients (411 + nom du client) et fournisseurs (401)
const isLetteredAccount = compteNum => compteNum.startsWith('411') || compteNum === '401000';

const normalize = text => String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Aperçu de l'export comptable de la période (ventes, acomptes, avoirs, achats, encaissements, décaissements) et téléchargement du FEC et du classeur Excel.
 */
export default {
  name: 'AccountingExportPanel',
  components: {AccountingControls},
  data() {
    return {
      PAGE_SIZE,
      exportData: undefined,
      isLoading: false,
      selectedJournals: [],
      onlyUnlettered: false,
      search: '',
      visibleCount: PAGE_SIZE,
    };
  },
  computed: {
    isAccountantModule() {
      return localStorage && localStorage.options && localStorage.options.includes('accountant');
    },
    periodData() {
      return AccountingService.data;
    },
    // Comptable : le professionnel sélectionné (undefined pour un professionnel sur ses propres données)
    professionalId() {
      return this.isAccountantModule ? AccountingService.data.selectedProfessional?.id : undefined;
    },
    // Écritures (groupes de lignes) avec leur texte de recherche et l'état de lettrage de leur compte client
    groups() {
      if (!this.exportData) return [];
      const byNumber = new Map();
      for (const entry of this.exportData.entries) {
        if (!byNumber.has(entry.ecritureNum)) {
          byNumber.set(entry.ecritureNum, {journalCode: entry.journalCode, lines: [], search: '', unlettered: false});
        }
        const group = byNumber.get(entry.ecritureNum);
        group.lines.push(entry);
        group.search += ` ${entry.compteNum} ${entry.compAuxNum} ${entry.compAuxLib} ${entry.pieceRef} ${entry.ecritureLib}`;
        group.unlettered = group.unlettered || (isLetteredAccount(entry.compteNum) && !entry.ecritureLet);
      }
      const groups = [...byNumber.values()];
      groups.forEach(group => { group.search = normalize(group.search); });
      return groups;
    },
    unletteredCount() {
      return this.groups.filter(group => group.unlettered).length;
    },
    isFiltered() {
      return this.selectedJournals.length > 0 || this.onlyUnlettered || !!this.search.trim();
    },
    // Les filtres s'appliquent à l'écriture entière : l'aperçu reste toujours équilibré
    displayedGroups() {
      const search = normalize(this.search.trim());
      return this.groups.filter(group =>
          (this.selectedJournals.length === 0 || this.selectedJournals.includes(group.journalCode))
          && (!this.onlyUnlettered || group.unlettered)
          && (!search || group.search.includes(search)));
    },
    visibleEntries() {
      return this.displayedGroups.slice(0, this.visibleCount).flatMap(group => group.lines);
    },
    hiddenCount() {
      return Math.max(0, this.displayedGroups.length - this.visibleCount);
    },
    displayedTotals() {
      const lines = this.displayedGroups.flatMap(group => group.lines);
      return {
        debit: lines.reduce((sum, entry) => sum + entry.debit, 0),
        credit: lines.reduce((sum, entry) => sum + entry.credit, 0),
      };
    },
    // Le groupe a l'option achats : les achats et leurs décaissements font partie de l'export
    hasPurchases() {
      return !!this.exportData && !!this.exportData.hasPurchases;
    },
    // Contenu des exports : les journaux sélectionnés (tous si aucun), indépendamment de la recherche et du filtre « Non lettrées »
    exportedData() {
      return restrictToJournals(this.exportData, this.selectedJournals);
    },
    // Rappel du périmètre sur les boutons d'export : les journaux sélectionnés, ou tous les journaux
    scopeLabel() {
      if (!this.exportedData) return '';
      return this.exportedData.selectedJournals ? `(${this.exportedData.selectedJournals.join(', ')})` : '(tous les journaux)';
    },
    canExport() {
      return !this.isLoading && !!this.exportData && this.exportData.entries.length > 0;
    },
  },
  watch: {
    "periodData.selectedPeriod"() {
      this.loadData();
    },
    // Nouveau filtre : retour au début de l'aperçu
    selectedJournals() { this.visibleCount = PAGE_SIZE; },
    onlyUnlettered() { this.visibleCount = PAGE_SIZE; },
    search() { this.visibleCount = PAGE_SIZE; },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    displayDate,
    money,
    // Période sélectionnée (bornes incluses côté back)
    buildUrlParams() {
      const period = this.periodData.selectedPeriod;
      return `?start=${encodeURIComponent(period.StartPeriodDate)}&end=${encodeURIComponent(period.EndPeriodDate)}`;
    },
    async loadData() {
      if (this.isAccountantModule && !this.professionalId) return;
      this.isLoading = true;
      this.exportData = undefined;
      this.selectedJournals = [];
      this.onlyUnlettered = false;
      this.search = '';
      this.visibleCount = PAGE_SIZE;
      const params = this.buildUrlParams();
      const resp = await AccountingController.getExport(params, this.professionalId);
      if (resp && !resp.errors) {
        // Plusieurs milliers de lignes en lecture seule : inutile de les rendre réactives
        resp.entries.forEach(Object.freeze);
        Object.freeze(resp.entries);
        this.exportData = resp;
      }
      this.isLoading = false;
    },
    // Sélection multiple : un clic ajoute ou retire le journal ; tous les journaux sélectionnés équivaut à aucun filtre
    toggleJournal(code) {
      const selected = this.selectedJournals.includes(code) ? this.selectedJournals.filter(current => current !== code) : [...this.selectedJournals, code];
      this.selectedJournals = selected.length === this.exportData.journals.length ? [] : selected;
    },
    exportFec() {
      downloadFec(this.exportedData, this.periodData.selectedPeriod.EndPeriodDate);
    },
    exportExcel() {
      downloadExcel(this.exportedData);
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.export-panel
  display flex
  flex-direction column
  gap 10px

.summary
  display flex
  flex-wrap wrap
  align-items center
  gap 10px

.summary-item
  display flex
  flex-direction column
  padding 8px 14px
  border 1px solid #dce5ea
  border-radius 8px
  background #f5f8fa

.summary-label
  font-size 12px
  color #526570

.summary-value
  font-size 16px
  font-weight bold

.actions
  display flex
  gap 8px
  margin-left auto

.export-button
  display flex
  align-items center
  gap 6px
  padding 8px 14px
  border 1px solid #b9ccd6
  border-radius 6px
  background #e4edf2
  color #304854
  font-weight bold
  cursor pointer

  &:hover:not(:disabled)
    background #d5e3eb

  &:disabled
    opacity .5
    cursor not-allowed

.button-scope
  font-size 12px
  font-weight normal

.filters
  display flex
  flex-wrap wrap
  gap 10px

.filter-group
  display flex
  flex-direction column
  flex 1 1 340px
  gap 6px
  padding 8px 12px
  border 1px solid #dce5ea
  border-radius 8px

// Groupe dont la sélection change le contenu de l'export
.export-group
  flex 2 1 520px
  border-color #5a9ee0
  background #f4f9fe

.filter-title
  display flex
  flex-wrap wrap
  align-items center
  gap 6px
  font-size 13px
  font-weight bold
  color #304854

.filter-tag
  padding 1px 8px
  border-radius 10px
  background #eaf0f4
  color #526570
  font-size 11px
  font-weight normal

.export-tag
  background #d6e8fb
  color #17497f

.journals
  display flex
  flex-wrap wrap
  gap 6px

.chip
  padding 5px 12px
  border 1px solid #dce5ea
  border-radius 20px
  background #f5f8fa
  color #526570
  font-size 12px
  cursor pointer

  &:hover
    background #eaf0f4

  &.active
    background #d6e8fb
    border-color #5a9ee0
    color #17497f
    font-weight bold

.chip-count
  margin-left 4px
  padding 0 6px
  border-radius 10px
  background #fff

.export-scope
  margin 0
  font-size 13px
  color #304854

.search
  flex 1 1 220px
  min-width 0
  padding 6px 12px
  border 1px solid #dce5ea
  border-radius 20px
  background #f5f8fa
  font-size 13px

  &:focus
    outline none
    border-color #b9ccd6

.help
  display flex
  flex-direction column
  gap 6px
  font-size 13px
  color #526570

  p, ul
    margin 0

  ul
    padding-left 20px

  li
    margin 2px 0

// Colonnes de largeur fixe (voir colgroup) : un texte trop long est tronqué par « … » à l'affichage (infobulle au survol), l'export n'est pas touché
.entries
  width 100%
  table-layout fixed
  border-collapse collapse
  font-size 14px

  th, td
    padding 6px 8px
    text-align left
    white-space nowrap
    overflow hidden
    text-overflow ellipsis

  th
    background #e4edf2
    color #304854

  tbody tr.alt
    background #f5f8fa

  .right
    text-align right

  .aux-lib
    margin-left 6px
    color #6b7076

  .total td
    font-weight bold
    background #b4bbd9

  .empty-row td
    text-align center
    padding 20px

  .more-row td
    text-align center
    padding 8px

.more-button
  border none
  background none
  color #304854
  font-weight bold
  text-decoration underline
  cursor pointer

.doc-chip
  padding 0 6px
  border-radius 5px
  font-size 0.8em
  font-weight 600
  line-height 1.6
  background-color #f0f1f3
  color #6b7076
</style>
